// /accounts — list + "Create account" Modal. B.1.5 promoted account
// creation from CLI-only to a UI form; the CLI still works for ops
// shells / containers.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { accounts } from "../../api/endpoints/accounts";
import { qk } from "../../api/queryKeys";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { EmptyState } from "../../components/ui/EmptyState";
import { Modal } from "../../components/ui/Modal";
import { Pill } from "../../components/ui/Pill";
import { Table } from "../../components/ui/Table";
import { PageHeader } from "../../components/shell/PageHeader";
import type { AccountOut } from "../../api/types/account";

import { AccountCreateForm } from "./AccountCreateForm";

export function AccountsListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const q = useQuery({
    ...adminQueryOptions,
    queryKey: qk.accounts(),
    queryFn: () => accounts.list().then((r) => r.data),
  });

  return (
    <>
      <PageHeader
        title="Accounts"
        subtitle="The billing/contractual entities that own tenants. Each tenant is one Home Assistant deployment under its account."
        actions={
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            style={primaryBtn}
          >
            Create account
          </button>
        }
      />
      {q.isPending && <p style={{ color: "var(--text-dim)" }}>Loading…</p>}
      {q.error && <ErrorBanner error={q.error} title="Failed to list accounts" />}
      {q.data && (
        <Table<AccountOut>
          columns={[
            {
              key: "slug",
              header: "Slug",
              render: (a) => (
                <Link
                  to="/accounts/$accountSlug"
                  params={{ accountSlug: a.slug }}
                  style={{ color: "var(--accent)" }}
                >
                  {a.slug}
                </Link>
              ),
            },
            { key: "display_name", header: "Display name" },
            {
              key: "account_kind",
              header: "Kind",
              render: (a) => <Pill tone="info">{a.account_kind}</Pill>,
            },
            { key: "status", header: "Status" },
          ]}
          rows={q.data}
          rowKey={(a) => a.id}
          empty={
            <EmptyState
              title="No accounts yet"
              description="Click 'Create account' above to add one. Existing Vault tenants can be imported as tenants under your new account with: docker exec orixen-fleet orixen-hub backfill-tenants --account-slug <slug>."
            />
          }
        />
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        closeDisabled={creating}
        title="Create account"
        width={480}
      >
        <AccountCreateForm
          onDone={() => setCreateOpen(false)}
          onPendingChange={setCreating}
        />
      </Modal>
    </>
  );
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};
